import { useState } from 'react';
import {
  Content,
  ContentHeader,
  Header,
  Page,
  SupportButton,
} from '@backstage/core-components';
import { CustomImportStepper } from './CustomImportStepper';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionLink from './AccordionLink';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(4),
  },
  accordionRoot: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '&::before': {
      display: 'none',
    },
  },
  accordionSummary: {
    justifyContent: 'start',
    padding: 0,
    height: '3rem',
    minHeight: '2rem !important', // Most sane way to override this minHeight here
  },
  accordionExpanded: {
    minHeight: '1.75rem',
  },
  accordionContent: {
    flexGrow: 0, // Prevent the content from growing to fill space, content being the accordion title
  },
  accordionDetails: {
    padding: 0,
  },
  bulletList: {
    padding: 0,
    paddingLeft: theme.spacing(3),
    listStyleType: 'disc',
    '& > li': {
      margin: theme.spacing(0.5, 0),
      padding: 0,
    },
  },
}));

export const CustomCatalogImportPage = () => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const classes = useStyles();

  const handleChange = () => {
    setExpanded(prev => !prev);
  };

  return (
    <Page themeId="home">
      <Header
        title="Register software"
        subtitle="Register your software in the Traton Developer Portal Catalog"
      />
      <Content className={classes.content}>
        <ContentHeader
          title="Register your software in the Traton Developer Portal Catalog"
          description="The wizard below analyses the configuration file, previews the entities and adds them to the Traton Developer Portal."
        >
          <SupportButton>
            Start tracking your software in Backstage by adding it to the
            catalog.
          </SupportButton>
        </ContentHeader>

        <Accordion
          expanded={expanded}
          onChange={handleChange}
          elevation={0}
          classes={{ root: classes.accordionRoot }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="prerequisites-content"
            id="prerequisites-header"
            classes={{
              content: classes.accordionContent,
              root: classes.accordionSummary,
              expanded: classes.accordionExpanded,
            }}
          >
            <Typography variant="h5">Prerequisites</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Box>
              <Typography variant="body1" paragraph>
                Before registering your software, ensure the following
                prerequisites are met:
              </Typography>

              <ul className={classes.bulletList}>
                <li>
                  <Typography variant="body1">
                    <strong>
                      Your team is registered in the Traton Developer Portal.
                    </strong>{' '}
                    <br />
                    If the software is owned by a team (not a single developer),
                    the team must be registered in the Traton Developer Portal.{' '}
                    <br />
                    <AccordionLink
                      href="/docs/default/component/traton-developer-portal-docs/use-portal/groups/"
                      external
                    >
                      Manage groups
                    </AccordionLink>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>Your GitLab repository contains a</strong>{' '}
                    <code>catalog-info.yaml</code> <strong>file.</strong> <br />{' '}
                    This file should include all the relevant information and
                    metadata about the software.
                    <br />
                    <AccordionLink
                      href="/docs/default/component/traton-developer-portal-docs/use-portal/onboard-portal/#create-a-catalog-infoyaml-file"
                      external
                    >
                      Create a catalog-info.yaml file
                    </AccordionLink>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>
                      A GitLab service account has access to the repository
                      containing the
                    </strong>{' '}
                    <code>catalog-info.yaml</code>
                    <strong> file.</strong>
                    <br /> This ensures the Traton Developer Portal can read and
                    register your software. <br />
                    <AccordionLink
                      href="/docs/default/component/traton-developer-portal-docs/use-portal/onboard-portal/#grant-access-to-a-dedicated-gitlab-service-account"
                      external
                    >
                      Grant access to a dedicated GitLab service account
                    </AccordionLink>
                  </Typography>
                </li>
              </ul>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Grid container px={2} spacing={2} mt={1.5}>
          <Grid item xs={12} md={8} lg={7} xl={8}>
            <CustomImportStepper />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
